/**
 * Alshallal Pastry Company HR System - Expiring Documents Badge
 * Phase 19: In-app notification for HR roles
 * Shows count of expiring/expired documents
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Clock, FileWarning } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ExpiringDocsBadge = () => {
  const { token, user } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Only show for HR roles
  const isHRRole = ['hr_manager', 'hr_admin', 'super_admin'].includes(user?.role);
  
  useEffect(() => {
    if (!token || !isHRRole) {
      setLoading(false);
      return;
    }
    
    const fetchSummary = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/notifications/expiring-documents-summary?window_days=30`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSummary(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch expiring docs summary:', err);
        setError('Failed to load');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSummary();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchSummary, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, isHRRole]);
  
  // Don't render for non-HR roles
  if (!isHRRole) return null;
  
  // Don't render if loading or no alerts
  if (loading || !summary || summary.total_alert_count === 0) return null;
  
  const hasExpired = summary.expired_count > 0;
  const hasExpiring = summary.expiring_count > 0;
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "relative",
            hasExpired && "text-red-600 hover:text-red-700"
          )}
          data-testid="expiring-docs-badge"
        >
          <FileWarning className={cn(
            "h-5 w-5",
            hasExpired ? "text-red-500" : "text-amber-500"
          )} />
          <Badge
            variant="destructive"
            className={cn(
              "absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs",
              hasExpired ? "bg-red-500" : "bg-amber-500"
            )}
          >
            {summary.total_alert_count > 99 ? '99+' : summary.total_alert_count}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          <div className="font-semibold flex items-center gap-2">
            <AlertCircle className={cn(
              "h-4 w-4",
              hasExpired ? "text-red-500" : "text-amber-500"
            )} />
            {isRTL ? 'تنبيهات المستندات' : 'Document Alerts'}
          </div>
          
          {hasExpired && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-red-600 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {isRTL ? 'منتهية الصلاحية' : 'Expired'}
              </span>
              <Badge variant="destructive" className="bg-red-500">
                {summary.expired_count}
              </Badge>
            </div>
          )}
          
          {hasExpiring && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-amber-600 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {isRTL ? `تنتهي خلال ${summary.window_days} يوم` : `Expiring in ${summary.window_days} days`}
              </span>
              <Badge className="bg-amber-500">
                {summary.expiring_count}
              </Badge>
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2"
            onClick={() => navigate('/admin/documents/expiring')}
            data-testid="view-expiring-docs-btn"
          >
            {isRTL ? 'عرض التفاصيل' : 'View Details'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ExpiringDocsBadge;
